import type { Idea, Settings } from "../data/types";
import { CLOUD_PROVIDER_DISPLAY, ideaStateLabel, isReadyForCloud } from "../data/types";
import type { ModelInfo } from "../data/modelStore";

interface Props {
  ideas: Idea[];
  settings: Settings;
  installedModel: ModelInfo | null;
  onNewIdea: () => void;
  onIdeaClick: (idea: Idea) => void;
  onOpenSettings: () => void;
}

export function HomeScreen(props: Props) {
  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Idea Triage</h1>
          <SettingsStatusChip settings={props.settings} installedModel={props.installedModel} />
        </div>
        <button className="btn-ghost" onClick={props.onOpenSettings}>
          Settings
        </button>
      </header>

      <main className="flex-1 px-4 py-4">
        {props.ideas.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="flex flex-col gap-3">
            {props.ideas.map((idea) => (
              <li key={idea.id}>
                <IdeaCard idea={idea} onClick={() => props.onIdeaClick(idea)} />
              </li>
            ))}
          </ul>
        )}
      </main>

      <button
        className="btn-primary fixed bottom-6 right-6 h-14 w-14 rounded-full text-2xl shadow-lg"
        onClick={props.onNewIdea}
        aria-label="Capture a new idea"
      >
        +
      </button>
    </>
  );
}

function IdeaCard(props: { idea: Idea; onClick: () => void }) {
  const { idea } = props;
  return (
    <button
      onClick={props.onClick}
      className="card w-full cursor-pointer p-4 text-left transition hover:shadow-md"
    >
      <div className="font-semibold">{idea.title}</div>
      {idea.rawCapture.length > 0 && (
        <div className="mt-1 line-clamp-2 text-sm text-ink-muted">{idea.rawCapture}</div>
      )}
      <div className="mt-3 inline-flex items-center rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent">
        {ideaStateLabel(idea.state)}
      </div>
    </button>
  );
}

function SettingsStatusChip(props: { settings: Settings; installedModel: ModelInfo | null }) {
  const provider = CLOUD_PROVIDER_DISPLAY[props.settings.selectedProvider];
  let label: string;
  let primary = false;
  if (props.settings.cloudEnabled) {
    if (isReadyForCloud(props.settings)) {
      label = `Cloud · ${provider} ✓`;
      primary = true;
    } else {
      label = `Cloud · ${provider} — no key`;
    }
  } else if (props.installedModel) {
    label = "On-device · Gemma ready";
    primary = true;
  } else {
    label = "On-device · model not installed";
  }
  return (
    <div
      className={`mt-0.5 text-xs font-medium ${primary ? "text-accent" : "text-ink-muted"}`}
    >
      {label}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-24 text-center">
      <h2 className="text-base font-semibold">No ideas yet.</h2>
      <p className="text-sm text-ink-muted">
        Tap + to capture one. Just dump the raw idea — we'll sharpen it later.
      </p>
    </div>
  );
}
